import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFile,
  faFilePdf,
  faTrashCan,
} from '@fortawesome/pro-duotone-svg-icons'
import Image from 'next/image'
import React from 'react'
import Spinner from '@/shared/components/Spinner'
import Link from 'next/link'
import { faUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons'

export type SupportingDocument = {
  name: string
  type: string
  url: string
  size?: number
  description: string
  uploadState: UploadState
}

type EditableDocument = {
  editable: true
  onChangeDescription: (description: string) => void
  onRemoveDocument: () => void
}

type NonEditableDocument = {
  editable: false
}

export type UploadState =
  | {
      uploading: true
      progress: number
    }
  | {
      uploading: false
      token: string
    }

type Props = {
  document: SupportingDocument
  enableDownload: boolean
} & (EditableDocument | NonEditableDocument)

export default function SupportingDocumentItem({
  document,
  enableDownload,
  ...props
}: Props) {
  const uploadState = document.uploadState

  let button = null
  let description = null

  if (uploadState.uploading) {
    button = (
      <div className="mx-3">
        <Spinner />
      </div>
    )
    description = (
      <div className="h-2.5 w-full bg-gray-200">
        <div
          className="h-2.5 bg-blue-600"
          style={{ width: `${100 * uploadState.progress}%` }}
        />
      </div>
    )
  } else if (props.editable) {
    button = (
      <button
        className="mx-8 text-gray-600 transition-colors hover:text-gray-800"
        type="button"
        onClick={() => props.onRemoveDocument()}
      >
        <FontAwesomeIcon icon={faTrashCan} size="xl" />
      </button>
    )
    description = (
      <textarea
        className="border-0 font-sans text-gray-900 ring-1 ring-transparent placeholder:text-gray-400 focus:ring-transparent sm:text-sm"
        rows={5}
        placeholder="Add your description..."
        value={document.description}
        onChange={(ev) => props.onChangeDescription(ev.target.value)}
      />
    )
  } else if (document.description.trim().length > 0) {
    description = (
      <p className="px-4 py-3 font-sans leading-6 text-gray-900 sm:text-sm">
        {document.description}
      </p>
    )
  }

  let preview = null
  if (document.type.startsWith('image')) {
    preview = (
      <Image
        src={document.url}
        alt={document.name}
        className="h-20 w-20 object-cover"
        unoptimized
        height="192"
        width="192"
      />
    )
  } else {
    const icon =
      document.type.endsWith('pdf') || document.type.endsWith('Pdf')
        ? faFilePdf
        : faFile
    preview = (
      <div className="flex h-20 w-20 items-center justify-center">
        <FontAwesomeIcon icon={icon} size="2xl" className="text-blue-800" />
      </div>
    )
  }

  return (
    <li className="flex w-full flex-col divide-y divide-gray-300 overflow-hidden rounded-md ring-1 ring-gray-300 focus-within:divide-blue-600 focus-within:ring-blue-600">
      <div className="flex items-center justify-between">
        {preview}
        <p className="grow truncate px-3 font-sans font-medium leading-6 text-gray-800">
          {enableDownload ? (
            <Link
              className="text-blue-600 transition-all hover:underline"
              href={document.url}
              target="_blank"
            >
              {document.name}
              <FontAwesomeIcon className="ml-2" icon={faUpRightFromSquare} />
            </Link>
          ) : (
            <>{document.name}</>
          )}
        </p>
        {button}
      </div>
      {description}
    </li>
  )
}
